// src/hooks/useDocumentUpload.ts
import { useState } from 'react';
import { toast } from 'react-toastify';
import { onUrlImageExtensionExist } from 'src/helpers/CommonHelpers';
import {
  checkFileFormat,
  UploadedFile,
  ValidatedFileFormat,
} from 'src/pages/SharedPageCustomHooks/UseDocumentUpload/helpers';

interface UseDocumentUploadOptions {
  documentType?: string | string[];
  maxSizeBytes?: number;
  onSuccess?: (file: UploadedFile, uploadType: 'Image' | 'Doc') => void;
  allowMultiple?: boolean;
}

interface UseDocumentUploadReturn {
  errorMessage: string;
  setErrorMessage: (message: string) => void;
  validateAndProcessFile: (file: UploadedFile) => boolean;
  validateAndProcessFiles: (files: UploadedFile[]) => UploadedFile[];
  isValidFileFormat: (
    file: UploadedFile,
    docType?: string | string[]
  ) => boolean;
  clearError: () => void;
}

export const useDocumentUpload = ({
  documentType,
  maxSizeBytes = 25 * 1024 * 1024,
  onSuccess,
  allowMultiple = false,
}: UseDocumentUploadOptions = {}): UseDocumentUploadReturn => {
  const [errorMessage, setErrorMessage] = useState<string>('');

  const clearError = () => {
    setErrorMessage('');
  };

  const validateFileSize = (file: UploadedFile): boolean => {
    if (file.size > maxSizeBytes) {
      const errorMsg = `File size exceeds the maximum allowed size of ${(maxSizeBytes / (1024 * 1024)).toFixed(0)}MB. Please try again with a smaller file.`;
      setErrorMessage(errorMsg);

      toast.error(errorMsg, {
        autoClose: 5000,
        position: 'top-center',
        toastId: 'file-size-error',
      });

      return false;
    }
    return true;
  };

  const isValidFileFormat = (
    file: UploadedFile,
    docType?: string | string[]
  ): boolean => {
    const typeToValidate: string | string[] = docType || documentType;

    const result: ValidatedFileFormat = checkFileFormat(
      file.name,
      typeToValidate
    );

    if (!result.isValid) {
      setErrorMessage(result.errorMessage || '');
      if (result.toastMessage) {
        toast.error(result.toastMessage, {
          autoClose: 12000,
          position: 'top-center',
        });
      }
    }

    return result.isValid;
  };

  const validateAndProcessFile = (file: UploadedFile): boolean => {
    try {
      if (!validateFileSize(file)) {
        return false;
      }

      if (!isValidFileFormat(file)) {
        return false;
      }

      const uploadType: 'Image' | 'Doc' = onUrlImageExtensionExist(file.name)
        ? 'Image'
        : 'Doc';

      clearError();

      if (onSuccess) {
        onSuccess(file, uploadType);
      }

      return true;
    } catch (error) {
      console.error('Document upload error:', error);

      const errorMsg =
        'Something went wrong with the document upload. Please try again.';
      setErrorMessage(errorMsg);

      toast.error(errorMsg, {
        autoClose: 5000,
        position: 'top-center',
        toastId: 'upload-error',
      });

      return false;
    }
  };

  const validateAndProcessFiles = (files: UploadedFile[]): UploadedFile[] => {
    if (!allowMultiple && files.length > 1) {
      const errorMsg = 'Only one file can be uploaded at a time.';
      setErrorMessage(errorMsg);
      toast.error(errorMsg, {
        autoClose: 5000,
        position: 'top-center',
      });
      return [];
    }

    const validFiles: UploadedFile[] = [];

    for (const file of files) {
      if (validateAndProcessFile(file)) {
        validFiles.push(file);
      }
    }

    return validFiles;
  };

  return {
    errorMessage,
    setErrorMessage,
    validateAndProcessFile,
    validateAndProcessFiles,
    isValidFileFormat,
    clearError,
  };
};
